import { Injectable } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import {
  catchError,
  concatMap,
  filter,
  mergeMap,
  Observable,
  of,
  switchMap,
  tap,
} from 'rxjs';
import { Action, Store } from '@ngrx/store';
import { Router } from '@angular/router';
import { WelcomePackHttpService } from '../../http/welcome-pack/welcome-pack-http.service';
import { WelcomePackStatusHttpModel } from '../../http/welcome-pack/welcome-pack-http.models';
import {
  WelcomePackActions,
  WelcomePackAPIActions,
} from '../actions/welcome-pack.actions';
import { SharedSelectors } from '../selectors';
import { LocalStorageKeysEnum } from '../../shared/enums';

@Injectable()
export class WelcomePackEffects {
  public loadWelcomePackStatus$: Observable<Action> = createEffect(() =>
    this._actions$.pipe(
      ofType(WelcomePackActions.loadWelcomePackStatus),
      switchMap(() =>
        this._welcomePackHttpService.welcomePackStatus$.pipe(
          mergeMap((welcomePackStatus) =>
            of(
              WelcomePackAPIActions.loadedWelcomePackStatusSuccessfully({
                welcomePackStatus,
              }),
            ),
          ),
          catchError((error) =>
            of(
              WelcomePackAPIActions.loadedWelcomePackStatusUnsuccessfully({
                error,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  public updateWelcomePackStep$: Observable<Action> = createEffect(() =>
    this._actions$.pipe(
      ofType(WelcomePackActions.updateWelcomePackStep),
      switchMap(({ step }) =>
        this._welcomePackHttpService.updateStep(step).pipe(
          filter((status) => status.body !== null),
          mergeMap(({ body: status }) =>
            of(
              WelcomePackAPIActions.updatedWelcomePackStatusSuccessfully({
                status: status as WelcomePackStatusHttpModel,
              }),
            ),
          ),
          catchError((error) =>
            of(
              WelcomePackAPIActions.updatedWelcomePackStatusUnsuccessfully({
                error,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  public incrementWelcomePackStep$: Observable<Action> = createEffect(() =>
    this._actions$.pipe(
      ofType(WelcomePackActions.incrementWelcomePackStep),
      concatLatestFrom(() =>
        this._store.select(SharedSelectors.selectWelcomePackNextStep),
      ),
      switchMap(([, step]) =>
        of(
          WelcomePackActions.updateWelcomePackStep({
            step,
          }),
        ),
      ),
    ),
  );

  public decrementWelcomePackStep$: Observable<Action> = createEffect(() =>
    this._actions$.pipe(
      ofType(WelcomePackActions.decrementWelcomePackStep),
      concatLatestFrom(() =>
        this._store.select(SharedSelectors.selectWelcomePackPreviousStep),
      ),
      switchMap(([, step]) =>
        of(
          WelcomePackActions.updateWelcomePackStep({
            step,
          }),
        ),
      ),
    ),
  );

  public finishedWelcomePackCheck$ = createEffect(() =>
    this._actions$.pipe(
      ofType(WelcomePackAPIActions.updatedWelcomePackStatusSuccessfully),
      filter(({ status: { isCompleted } }) => isCompleted),
      concatMap(() => of(WelcomePackActions.finishedWelcomePack())),
    ),
  );

  public closeWelcomePack$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(
          WelcomePackActions.closeWelcomePack,
          WelcomePackActions.finishedWelcomePack,
        ),
        tap(() => this._router.navigateByUrl('/dashboard').then()),
      ),
    { dispatch: false },
  );

  public navigateToWelcomePackIfNotStarted$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(WelcomePackAPIActions.loadedWelcomePackStatusSuccessfully),
        tap(({ welcomePackStatus: { currentStep } }) => {
          if (currentStep === null) {
            // note: log-in-target override
            localStorage.setItem(
              LocalStorageKeysEnum.reRouteAfterLogIn,
              '/dashboard/welcome-pack',
            );
            this._router.navigateByUrl('/dashboard/welcome-pack').then();
          }
        }),
      ),
    { dispatch: false },
  );

  constructor(
    private _actions$: Actions,
    private _welcomePackHttpService: WelcomePackHttpService,
    private _store: Store,
    private _router: Router,
  ) {}
}
